import { Badge, Button, Group, Menu, UnstyledButton } from '@mantine/core';
import { BRAND_NAME, type Capability, coordinatorLabel } from '@reditelji/shared';
import { type CSSProperties, useState } from 'react';
import { NavLink } from 'react-router';
import { useChangelog, useEvent } from '../api/hooks';
import { useSession } from '../api/session';
import type { TimelinePayload } from '../api/types';
import { dayToneAt } from '../lib/ui';
import { useUi } from '../state/ui';
import { deriveHeaderStats } from './headerStats';
import { NameDialog } from './NameDialog';
import { useLogout } from './useLogout';
import './header.css';

/** A tab asks for any one of `needs`; without the list it is everyone's (SPEC §1.1L). */
const TABS: { to: string; label: string; needs?: Capability[] }[] = [
  { to: '/pregled', label: 'Pregled' },
  { to: '/casovnica', label: 'Časovnica' },
  { to: '/predaje', label: 'Predaje' },
  { to: '/mesta', label: 'Mesta' },
  { to: '/prostovoljci', label: 'Prostovoljci' },
  { to: '/zemljevid', label: 'Zemljevid' },
  { to: '/sporocila', label: 'Sporočila', needs: ['comms:write'] },
  { to: '/nastavitve', label: 'Nastavitve', needs: ['settings:write', 'access:manage'] },
];

export function AppHeader({
  me,
  timeline,
}: {
  me: { name: string; email: string };
  timeline: TimelinePayload | undefined;
}) {
  const logout = useLogout();
  const [renaming, setRenaming] = useState(false);
  const event = useEvent();
  const changelog = useChangelog();
  const { can } = useSession();
  const { selectedDayId, setSelectedDay, setChangelogOpen } = useUi();

  const days = event.data?.days ?? [];
  const stats = timeline ? deriveHeaderStats(timeline) : null;
  const unseen = changelog.data?.unseenCount ?? 0;

  const selectedIndex = days.findIndex((d) => d.id === selectedDayId);
  const bar = selectedIndex >= 0 ? dayToneAt(selectedIndex).header : null;

  return (
    <header
      className="hd"
      style={
        bar
          ? ({
              '--hd-bar': bar.bar,
              '--hd-rail': bar.rail,
              '--hd-track': bar.track,
              '--hd-cov-track': bar.covTrack,
              '--hd-btn-bd': bar.btnBd,
            } as CSSProperties)
          : undefined
      }
    >
      <div className="hd-brand">
        <img className="hd-logo" src="/brand/jatr-logo.png" alt={BRAND_NAME} />
        <div className="hd-brand-text">
          <div className="hd-title">Razpored redarjev</div>
          <div className="hd-sub">
            {event.data?.name ?? '…'} · {can('plan:write') ? 'organizacijska ekipa' : 'vpogled za organizacijo'}
          </div>
        </div>
      </div>

      <nav className="hd-tabs">
        {TABS.filter((t) => !t.needs || t.needs.some(can)).map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            className={({ isActive }) => `hd-tab${isActive ? ' hd-tab--active' : ''}`}
          >
            {t.label}
          </NavLink>
        ))}
      </nav>

      <div className="hd-days">
        {days.map((d, i) => {
          const tone = dayToneAt(i).header;
          const active = d.id === selectedDayId;
          const short = days.length > 2 ? d.label.slice(0, 3) : d.label;
          return (
            <UnstyledButton
              key={d.id}
              className={`hd-day${active ? ' hd-day--active' : ''}`}
              // The bar already carries the day's colour, so the selected pill
              // reads as a white chip lifted off it rather than a second tint.
              style={active ? { color: tone.ink } : undefined}
              title={d.label}
              onClick={() => setSelectedDay(d.id)}
            >
              <span className="hd-day-dot" style={{ background: tone.dot }} />
              {short}
            </UnstyledButton>
          );
        })}
      </div>

      {stats ? (
        <>
          <div className="hd-cov" title="delež potrjenih izmen">
            <div className="hd-cov-label">POKRITOST</div>
            <Group gap={6} align="center">
              <span className="hd-cov-val">{stats.coveragePct}%</span>
              <span className="hd-cov-bar">
                <span className="hd-cov-fill" style={{ width: `${stats.coveragePct}%` }} />
              </span>
            </Group>
          </div>
          <div className="hd-stats" title="izmene / osebe">
            <StatChip label="potrjeno" value={stats.confirmedShifts} people={stats.confirmedPeople} />
            <StatChip
              label="čaka"
              value={stats.waitingShifts}
              people={stats.waitingPeople}
              color="oklch(0.55 0.1 75)"
            />
            {stats.rejectedShifts > 0 ? (
              <StatChip
                label="zavrnjeno"
                value={stats.rejectedShifts}
                people={stats.rejectedPeople}
                color="oklch(0.52 0.14 25)"
              />
            ) : null}
            {stats.externalShifts > 0 ? (
              <StatChip
                label="zunanje"
                value={stats.externalShifts}
                people={stats.externalPeople}
                square
              />
            ) : null}
            <StatChip label="nepokrito" value={stats.uncovered} color="oklch(0.55 0.12 25)" />
          </div>
        </>
      ) : null}

      <div className="hd-right">
        {can('audit:read') ? (
          <Button
            className="hd-log"
            variant="default"
            size="compact-sm"
            title="Dnevnik sprememb"
            onClick={() => setChangelogOpen(true)}
            rightSection={
              unseen > 0 ? (
                // Not `circle`: that forces width to equal height, which ellipses
                // any count of two digits or more. The pill keeps single digits
                // round (min-width in CSS) and grows for "12" / "99+".
                <Badge className="hd-log-badge" size="xs" color="red" variant="filled">
                  {unseen > 99 ? '99+' : unseen}
                </Badge>
              ) : null
            }
          >
            Dnevnik
          </Button>
        ) : null}
        <Menu position="bottom-end" withArrow>
          <Menu.Target>
            <UnstyledButton className="hd-user">{coordinatorLabel(me)}</UnstyledButton>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item onClick={() => setRenaming(true)}>Moje ime…</Menu.Item>
            <Menu.Item onClick={logout}>Odjava</Menu.Item>
          </Menu.Dropdown>
        </Menu>
        {renaming ? <NameDialog current={me.name} required={false} onClose={() => setRenaming(false)} /> : null}
      </div>
    </header>
  );
}

function StatChip({
  label,
  value,
  people,
  color,
  square,
}: {
  label: string;
  value: number;
  people?: number;
  color?: string;
  /** Marks an external-team figure — the square that means "not ours" (§1.1J). */
  square?: boolean;
}) {
  return (
    <div className="hd-chip">
      <span className="hd-chip-val" style={color ? { color } : undefined}>
        {value}
        {people !== undefined ? <span className="hd-chip-people">/{people}</span> : null}
      </span>
      <span className="hd-chip-label">
        {square ? (
          <span
            aria-hidden
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: 1,
              marginRight: 4,
              background: 'oklch(0.58 0.014 90)',
            }}
          />
        ) : null}
        {label}
      </span>
    </div>
  );
}
